<script setup>
import axios from 'axios'
import { computed, reactive, ref } from 'vue'
import { RouterLink, useRouter } from 'vue-router'
import { registerUser } from '@/services/backendService.js'

const router = useRouter()
const isSubmitting = ref(false)
const formError = ref('')
const form = reactive({
  username: '',
  password: '',
  passwordConfirmation: '',
  registrationToken: '',
})

const passwordsMatch = computed(
  () => form.password === form.passwordConfirmation,
)

const validateForm = () => {
  if (!form.username.trim()) return 'Login or email is required.'
  if (!form.password) return 'Password is required.'
  if (!form.passwordConfirmation) return 'Repeat password is required.'
  if (!passwordsMatch.value) return 'Passwords do not match.'
  if (!form.registrationToken.trim()) return 'Registration code is required.'

  return ''
}

const messageForError = (error) => {
  if (!axios.isAxiosError(error)) {
    return 'Registration failed. Please try again.'
  }

  if (!error.response) {
    return 'Server unavailable. Please try again later.'
  }

  if (error.response.status === 400) return 'Passwords do not match.'
  if (error.response.status === 403) return 'Invalid registration code.'
  if (error.response.status === 409) return 'This login or email is already registered.'
  if (error.response.status === 422) return 'Check the form fields and try again.'
  if (error.response.status === 429) return 'Too many registration attempts. Try again in a minute.'
  if (error.response.status >= 500) return 'Server unavailable. Please try again later.'

  return 'Registration failed. Please try again.'
}

const submitRegistration = async () => {
  if (isSubmitting.value) return

  formError.value = validateForm()
  if (formError.value) return

  isSubmitting.value = true
  try {
    await registerUser({
      username: form.username.trim(),
      password: form.password,
      passwordConfirmation: form.passwordConfirmation,
      registrationToken: form.registrationToken.trim(),
    })
    await router.push({
      path: '/login',
      query: { registered: '1' },
    })
  } catch (error) {
    formError.value = messageForError(error)
  } finally {
    isSubmitting.value = false
  }
}
</script>

<template>
  <section class="registration-page">
    <header class="page-header">
      <p class="eyebrow">Private beta</p>
      <h1>Create account</h1>
    </header>

    <form class="registration-form" @submit.prevent="submitRegistration">
      <label class="field-label">
        Login / Email
        <input
          v-model="form.username"
          class="form-input"
          type="text"
          name="username"
          autocomplete="username"
          maxlength="255"
          required
          :disabled="isSubmitting"
        />
      </label>

      <label class="field-label">
        Password
        <input
          v-model="form.password"
          class="form-input"
          type="password"
          name="password"
          autocomplete="new-password"
          required
          :disabled="isSubmitting"
        />
      </label>

      <label class="field-label">
        Repeat password
        <input
          v-model="form.passwordConfirmation"
          class="form-input"
          type="password"
          name="passwordConfirmation"
          autocomplete="new-password"
          required
          :disabled="isSubmitting"
        />
      </label>

      <label class="field-label">
        Registration code
        <input
          v-model="form.registrationToken"
          class="form-input"
          type="password"
          name="registrationToken"
          autocomplete="off"
          required
          :disabled="isSubmitting"
        />
      </label>

      <p v-if="formError" class="form-message error" role="alert">{{ formError }}</p>

      <div class="form-actions">
        <button class="action-button primary" type="submit" :disabled="isSubmitting">
          {{ isSubmitting ? 'Creating account...' : 'Create account' }}
        </button>
        <RouterLink class="secondary-link" to="/login">Back to login</RouterLink>
      </div>
    </form>
  </section>
</template>

<style scoped>
.registration-page {
  padding: 2rem 1.5rem 3rem;
}

.page-header {
  display: grid;
  gap: 0.55rem;
  margin-bottom: 1.5rem;
}

.eyebrow {
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-size: 0.75rem;
  color: #7acb8a;
}

h1 {
  margin: 0;
  font-size: 2.2rem;
  color: #f6f7f2;
}

.registration-form {
  display: grid;
  gap: 1rem;
  max-width: 28rem;
  padding-top: 1.25rem;
  border-top: 1px solid rgba(122, 203, 138, 0.18);
}

.field-label {
  display: grid;
  gap: 0.4rem;
  color: #aee0b6;
  font-size: 0.85rem;
  font-weight: 700;
}

.form-input {
  width: 100%;
  border: 1px solid rgba(174, 224, 182, 0.28);
  background: rgba(7, 14, 10, 0.62);
  color: #f6f7f2;
  border-radius: 8px;
  padding: 0.7rem 0.8rem;
  font: inherit;
}

.form-input:focus {
  border-color: #9fe2ab;
  outline: none;
}

.form-input:disabled {
  opacity: 0.7;
}

.form-message {
  margin: 0;
  font-size: 0.9rem;
}

.form-message.error {
  color: #ffb4ab;
}

.form-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.action-button {
  border: 1px solid rgba(174, 224, 182, 0.38);
  background: rgba(255, 255, 255, 0.08);
  color: #eef3eb;
  border-radius: 8px;
  min-height: 2.35rem;
  padding: 0.6rem 0.95rem;
  cursor: pointer;
  font-weight: 700;
  white-space: nowrap;
}

.action-button:hover:not(:disabled) {
  border-color: #9fe2ab;
  background: rgba(122, 203, 138, 0.16);
}

.action-button:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}

.action-button.primary {
  background: #4caf50;
  border-color: #4caf50;
  color: #08120a;
}

.secondary-link {
  color: #aee0b6;
}

@media (max-width: 700px) {
  .registration-page {
    padding: 1.2rem 1rem 2rem;
  }

  .registration-form {
    max-width: none;
  }
}
</style>
